import React from 'react'
import { OrderedMap, Set } from 'immutable'
import _ from 'lodash'
import { pipe } from 'lib/functional'
import queryString from 'query-string'
import { FormattedMessage as T, defineMessages } from 'react-intl'

// These constants are used to set and determine the different states of the smart export button
export const SMART_EXPORT_DEFAULT = 'default'
export const SMART_EXPORT_PHONE_PUSH = 'phone_push'
export const SMART_EXPORT_FILE_DOWNLOAD = 'file_download'

const formatMessages = defineMessages({
  fit: {
    id: 'formats.fit',
    defaultMessage: 'FIT Course',
    description: 'FIT is a filetype, please do not translate'
  },
  tcx: {
    id: 'formats.tcx',
    defaultMessage: 'TCX Course',
    description: 'TCX is a filetype, please do not translate'
  },
  tcxh: {
    id: 'formats.tcxh',
    defaultMessage: 'TCX History',
    description: 'TCX is a filetype, please do not translate'
  },
  gpxt: {
    id: 'formats.gpxt',
    defaultMessage: 'GPX Track',
    description: 'GPX is a filetype, please do not translate'
  },
  kml: {
    id: 'formats.kml',
    defaultMessage: 'Google Earth'
  },
  gpxr: {
    id: 'formats.gpxr',
    defaultMessage: 'GPX Route',
    description: 'GPX is a filetype, please do not translate'
  },
  csv: {
    id: 'formats.csv',
    defaultMessage: 'Cuesheet CSV',
    description: 'CSV is a file format and stands for Comma Separated Values'
  }
})

export const formatsForObjectType = (objectType) => OrderedMap(
  Object.assign(
    {
      fit: {
        name: 'FIT Course',
        msg: formatMessages.fit,
        ext: 'fit',
        defaultExpanded: 'route',
        description: <T
          id='formats.fitDescription'
          defaultMessage='FIT is the best format for turn-by-turn directions on modern <strong>Garmin Edge Devices</strong>.'
          values={{ strong: (...c) => <strong>{c}</strong> }} />,
        supportedOptions: Set(['turnNotificationDistance']),
        smartExportable: true
      },
      tcx: {
        name: 'TCX Course',
        msg: formatMessages.tcx,
        ext: 'tcx',
        description: <T
          id='formats.tcxDescription'
          defaultMessage='Compatible with most <strong>modern GPS units</strong>. Provides turn by turn directions including custom cue entries, but may consume more space than FIT.'
          values={{ strong: (...c) => <strong>{c}</strong> }} />,
        supportedOptions: objectType === 'route' ? Set(['turnNotificationDistance', 'reduceTo']) : Set(['reduceTo']),
        smartExportable: true
      },
      tcxh: {
        name: 'TCX History',
        msg: formatMessages.tcxh,
        ext: 'tcx',
        defaultExpanded: 'trip',
        subFormat: 'history',
        description: <T
          id='formats.tcxHDescription'
          defaultMessage='Useful for uploading your activity to another service, keeping records on your own computer etc.' />,
        supportedOptions: Set(),
        smartExportable: false
      },
      gpxt: {
        name: 'GPX Track',
        msg: formatMessages.gpxt,
        ext: 'gpx',
        subFormat: 'track',
        description: <T
          id='gpxTDescripion'
          defaultMessage='Useful for any GPS unit. <strong>Contains no cuesheet entries</strong>, only track information (breadcrumb trail).'
          values={{ strong: (...c) => <strong>{c}</strong> }} />,
        supportedOptions: Set(['poiAsWpt', 'cuesAsWpt', 'reduceTo']),
        smartExportable: true
      },
      kml: {
        name: 'Google Earth',
        msg: formatMessages.kml,
        ext: 'kml',
        description: <T
          id='formats.googleEarthDescription'
          defaultMessage='Allows you to view this route in Google Earth.' />,
        supportedOptions: Set()
      }
    }, objectType === 'route' && {
      gpxr: {
        name: 'GPX Route',
        msg: formatMessages.gpxr,
        ext: 'gpx',
        subFormat: 'route',
        description: <T
          id='gpxRDescripion'
          defaultMessage='Only the cuesheet entries, not the entire track. Not useful for most, you most likely want a GPX Track.' />,
        supportedOptions: Set()
      },
      csv: {
        name: 'Cuesheet CSV',
        msg: formatMessages.csv,
        ext: 'csv',
        description: <T
          id='formats.csvDescription'
          defaultMessage='Only the cue sheet. You can print it from Excel or another spreadsheet program.' />,
        supportedOptions: Set()
      }
    }
  )
)

export const fileDownloadURL = (format, id, objectType, activeOptions = Set(), privacyCode, turnNotificationDistance) => {
  const { supportedOptions } = format

  const filteredOptions = activeOptions
    .filter((k) => supportedOptions.has(k))
    .toMap()
    .map(() => true)

  const mergeIf = (condition, toMerge) => (options) => (
    condition ? options.merge(toMerge) : options
  )

  const queryOptions = pipe(
    mergeIf(filteredOptions.has('turnNotificationDistance'), { turnNotificationDistance }),
    mergeIf(filteredOptions.has('reduceTo'), { reduceTo: 500 }),
    mergeIf(format.subFormat, { subFormat: format.subFormat }),
    mergeIf(privacyCode, { privacyCode: privacyCode })
  )(filteredOptions)

  const query = queryString.stringify(
    queryOptions.mapKeys(k => _.snakeCase(k)).toJS()
  )

  const urlBase = format.pathOverride
    ? `/${objectType}s/${id}${format.pathOverride}`
    : `/${objectType}s/${id}.${format.ext}`

  return query
    ? `${urlBase}?${query}`
    : urlBase
}
